import React, { useState, useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

/**
 * This is a wrapper around react-datepicker.
 * react-datepicker deals with Date objects but Gear primarily deals with ISO 8601 date strings (yyyy-mm-dd).
 * This component will handle the conversion between Date and String.
 * Any react-datepicker properties can be passed in and will be relayed to ReactDatePicker using the ...rest syntax. 
 * 
 * See https://reactdatepicker.com/ and https://github.com/Hacker0x01/react-datepicker/blob/master/docs/datepicker.md
 * 
 * @param props.dateStr The ISO 8601 date String (yyyy-mm-dd)
 * @param props.onChange The function to call when the date is changed
 * @param props.forwardedRef An object in which to store a reference to the react date picker
 * @param props.maxDate (optional) The max date value to use. Will default to today if no value is passed in
 * @param props Props to override ReactDatePicker defaults
 */
const DatePicker = ({ dateStr, onChange, forwardedRef, maxDate, ...props }) => {
	const [selectedDate, setSelectedDate] = useState(null);

	/**
	 * Time Picker Example
() => {
  const [startDate, setStartDate] = useState(new Date());
  return (
    <DatePicker
      selected={startDate}
      onChange={date => setStartDate(date)}
      showTimeInput
      showTimeSelectOnly
      inline
      timeInputLabel=""
      dateFormat="h:mm aa"
    />
  );
};
	 */

	useEffect(() => {
		// Convert the date String into a Date object
		setSelectedDate(getDateFromIso8601String(dateStr));
	}, [dateStr]);
	
	
	const submitChange = (date) => {
		setSelectedDate(date);
		if (onChange) {
			// Convert the Date object back into a String before calling onChange
			onChange(convertDateToIso8601String(date));
		}
	}

	// The minimum date to use in the D.O.B. date picker
	let minDate = new Date();
	minDate.setFullYear(minDate.getFullYear() - 150);

	/*
	 * Fields passed in props will override any of these settings.
	 * For example, <DatePicker showMonthDropdown={false} /> will override the default 'true' value.
	 */
	return (
		<ReactDatePicker
			selected={selectedDate}
			onChange={(date) => submitChange(date)}
			minDate={minDate}
			maxDate={maxDate || new Date()}
			showMonthDropdown
			showYearDropdown
			//yearDropdownItemNumber={110}
			scrollableYearDropdown={true}
			dropdownMode="select" // 'select' or 'scroll'
			placeholderText="mm/dd/yyyy"
			dateFormat={["MM/dd/yyyy", "M/dd/yyyy", "M/d/yyyy", "MM/d/yyyy", "MM/dd/yy", "M/dd/yy", "M/d/yy", "MMddyyyy", "Mdyy", "Mdyyyy"]}
			className="form-control"
			enableTabLoop={false}
			ref={forwardedRef}
			strictParsing
			{...props}
		/>
	);
};

export default DatePicker;

/**
 * Calculates years since a given date.
 *
 * @param dob The date
 * @return The age (years since the given date)
 */
export const calculateAge = (dob) => {
	if (dob) {
		var ageDifMs = Date.now() - dob;
		var ageDate = new Date(ageDifMs); // milliseconds from epoch
		return ageDate.getUTCFullYear() - 1970;
	}
};

/**
 * Formats a Date object into yyyy-mm-dd format.
 * The month and day are padded with leading zeros if needed.
 * Returns null when date is null or undefined.
 *
 * @param date The Date object to format
 * @return The date in 'yyyy-mm-dd' format
 */
export const convertDateToIso8601String = (date) => {
	if (!date) {
		return null;
	}
	const year = date.getFullYear();

	let month = (1 + date.getMonth()).toString();
	month = month.length > 1 ? month : '0' + month;

	let day = date.getDate().toString();
	day = day.length > 1 ? day : '0' + day;

	return `${year}-${month}-${day}`;
};

/**
 * Converts a String in the format 'yyyy-mm-dd' or `yyyy-mm-dd'T'HH:mm:ss` into a Date object.
 *
 * @param dateStr The String to convert into a Date
 * @return The Date object
 */
export const getDateFromIso8601String = (dateStr) => {
	if (!dateStr) {
		return null;
	}
	const [year, month, day] = (dateStr.split('T')[0]).split('-');

	return new Date(year, month - 1, day);
};

